import React, { Fragment, useEffect, useMemo, useState } from "react";
import API from "../../services/API";
import { toast } from "react-toastify";

const Settings = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [existingLogo, setExistingLogo] = useState(null);
  const [passwordUpdating, setPasswordUpdating] = useState(false);

  const passwordRules = useMemo(() => {
    return {
      minLength: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      digit: /\d/.test(newPassword),
    };
  }, [newPassword]);

  const isPasswordValid = useMemo(() => {
    return Object.values(passwordRules).every(Boolean);
  }, [passwordRules]);

  useEffect(() => {
    // Backend/localStorage key masih guna nama lama untuk elak breaking change.
    const savedLogo = localStorage.getItem("school_logo_url");
    if (savedLogo) {
      setExistingLogo(savedLogo);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleNewPasswordSubmit = async () => {
    if (!oldPassword) {
      toast.error("Sila masukkan kata laluan semasa", { autoClose: 2500 });
      return;
    }

    if (!newPassword) {
      toast.error("Sila masukkan kata laluan baharu", { autoClose: 2500 });
      return;
    }

    if (!isPasswordValid) {
      toast.error(
        "Kata laluan mesti sekurang-kurangnya 8 aksara dan mempunyai huruf besar, huruf kecil serta nombor",
        { autoClose: 3000 }
      );
      return;
    }

    setPasswordUpdating(true);

    try {
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: localStorage.getItem("token"),
      };

      const res = await API.post(
        "/schoolStaff/updatePassword",
        {
          current_password: oldPassword,
          new_password: newPassword,
        },
        { headers }
      );

      if (res.data.response_code === 1000) {
        toast.success(res.data.message || "Kata laluan berjaya dikemaskini", {
          autoClose: 4000,
        });

        setOldPassword("");
        setNewPassword("");
      } else {
        toast.error(res.data.message || "Gagal mengemaskini kata laluan", {
          autoClose: 4000,
        });
      }
    } catch (error) {
      if (error.response) {
        toast.error(
          `Ralat ${error.response.status}: ${
            error.response.data?.message || error.message
          }`,
          { autoClose: 5000 }
        );
      } else if (error.request) {
        toast.error("Tiada respons daripada pelayan", { autoClose: 5000 });
      } else {
        toast.error(`Ralat: ${error.message}`, { autoClose: 5000 });
      }
    } finally {
      setPasswordUpdating(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Hanya fail gambar dibenarkan", { autoClose: 2500 });
      return;
    }

    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error("Sila pilih logo terlebih dahulu", { autoClose: 2500 });
      return;
    }

    const formData = new FormData();
    formData.append("logo", logoFile);

    setUploading(true);

    try {
      const res = await API.post("/portal/uploadSchoolLogo", formData, {
        headers: {
          token: localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.response_code === 1000) {
        const logoUrl = res.data.data?.logo_url;

        if (logoUrl) {
          const freshLogo = `${logoUrl}?v=${Date.now()}`;

          // Backend/localStorage key masih guna nama lama untuk elak breaking change.
          localStorage.setItem("school_logo_url", freshLogo);
          window.dispatchEvent(new Event("school-logo-updated"));

          setExistingLogo(freshLogo);
          setLogoPreview(null);
          setLogoFile(null);
        }

        toast.success(res.data.message || "Logo berjaya dikemaskini", {
          autoClose: 3000,
        });
      } else {
        toast.error(res.data.message || "Gagal memuat naik logo", {
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Gagal memuat naik logo", { autoClose: 3000 });
    } finally {
      setUploading(false);
    }
  };

  const clearLogoSelection = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoFile(null);
    setLogoPreview(null);
  };

  return (
    <Fragment>
      <style>{`
        .settings-page {
          padding: 24px;
          background: #f8fafc;
          min-height: calc(100vh - 80px);
        }

        .settings-hero {
          background: linear-gradient(135deg, #0B4C9C 0%, #0F65C9 55%, #1E88E5 100%);
          border-radius: 28px;
          padding: 30px;
          color: #ffffff;
          margin-bottom: 24px;
          box-shadow: 0 24px 70px rgba(11, 76, 156, 0.18);
          position: relative;
          overflow: hidden;
        }

        .settings-hero::after {
          content: "";
          position: absolute;
          width: 260px;
          height: 260px;
          border-radius: 999px;
          right: -90px;
          top: -120px;
          background: rgba(255, 255, 255, 0.08);
        }

        .settings-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 14px;
        }

        .settings-eyebrow::before {
          content: "";
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: #22c55e;
          box-shadow: 0 0 0 5px rgba(34, 197, 94, 0.14);
        }

        .settings-hero h2 {
          margin: 0;
          font-size: 30px;
          font-weight: 900;
          letter-spacing: -0.04em;
        }

        .settings-hero p {
          margin: 10px 0 0;
          max-width: 720px;
          color: rgba(255, 255, 255, 0.82);
          font-size: 14px;
          line-height: 1.6;
        }

        .settings-grid {
          display: grid;
          grid-template-columns: 1fr 0.85fr;
          gap: 22px;
          align-items: stretch;
        }

        .settings-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 26px;
          box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
          overflow: hidden;
        }

        .settings-card-header {
          padding: 22px 24px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }

        .settings-card-kicker {
          color: #0B4C9C;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }

        .settings-card-title {
          margin: 0;
          color: #0f172a;
          font-size: 20px;
          font-weight: 900;
          letter-spacing: -0.03em;
        }

        .settings-card-subtitle {
          margin: 8px 0 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.55;
        }

        .settings-card-body {
          padding: 24px;
        }

        .settings-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }

        .settings-form-group label {
          display: block;
          color: #334155;
          font-size: 13px;
          font-weight: 800;
          margin-bottom: 8px;
        }

        .password-field {
          position: relative;
        }

        .settings-input {
          height: 50px;
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 16px;
          padding: 0 48px 0 14px;
          color: #0f172a;
          font-size: 14px;
          font-weight: 600;
          outline: none;
          transition: all 0.2s ease;
          background: #ffffff;
        }

        .settings-input:focus {
          border-color: #0B4C9C;
          box-shadow: 0 0 0 4px rgba(11, 76, 156, 0.08);
        }

        .eye-button {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          width: 34px;
          height: 34px;
          border: 0;
          border-radius: 999px;
          background: #f1f5f9;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .eye-button:hover {
          background: #dbeafe;
          color: #0B4C9C;
        }

        .password-rules {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 18px;
          padding: 16px;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: #f8fafc;
        }

        .password-rule {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
        }

        .rule-dot {
          width: 18px;
          height: 18px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #e2e8f0;
          color: #64748b;
          font-size: 10px;
          font-weight: 900;
        }

        .password-rule.valid {
          color: #166534;
        }

        .password-rule.valid .rule-dot {
          background: #dcfce7;
          color: #16a34a;
        }

        .settings-actions {
          margin-top: 22px;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        .primary-action {
          min-height: 46px;
          padding: 0 20px;
          border-radius: 14px;
          border: 0;
          background: #0B4C9C;
          color: #ffffff;
          font-size: 14px;
          font-weight: 900;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .primary-action:hover {
          background: #0F65C9;
          transform: translateY(-1px);
        }

        .primary-action:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .secondary-action {
          min-height: 42px;
          padding: 0 16px;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #475569;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
        }

        .logo-dropzone {
          border: 1px dashed #cbd5e1;
          border-radius: 22px;
          background: #f8fafc;
          padding: 22px;
          text-align: center;
          transition: all 0.2s ease;
        }

        .logo-dropzone:hover {
          border-color: #0B4C9C;
          background: #eff6ff;
        }

        .logo-upload-label {
          cursor: pointer;
          display: block;
        }

        .logo-upload-icon {
          width: 58px;
          height: 58px;
          border-radius: 18px;
          background: #eff6ff;
          color: #0B4C9C;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 900;
          margin-bottom: 12px;
          border: 1px solid #bfdbfe;
        }

        .logo-upload-title {
          color: #0f172a;
          font-size: 15px;
          font-weight: 900;
          margin-bottom: 4px;
        }

        .logo-upload-hint {
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
        }

        .logo-file-input {
          display: none;
        }

        .logo-preview-panel {
          margin-top: 18px;
          padding: 16px;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .logo-preview-label {
          color: #334155;
          font-size: 12px;
          font-weight: 900;
          margin-bottom: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .logo-preview-box {
          min-height: 130px;
          border-radius: 18px;
          background:
            linear-gradient(45deg, #f8fafc 25%, transparent 25%),
            linear-gradient(-45deg, #f8fafc 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #f8fafc 75%),
            linear-gradient(-45deg, transparent 75%, #f8fafc 75%);
          background-size: 18px 18px;
          background-position: 0 0, 0 9px, 9px -9px, -9px 0px;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 14px;
        }

        .logo-preview-box img {
          max-height: 120px;
          max-width: 100%;
          object-fit: contain;
          border-radius: 12px;
        }

        .logo-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-top: 18px;
        }

        .logo-filename {
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        @media (max-width: 991px) {
          .settings-grid {
            grid-template-columns: 1fr;
          }

          .settings-form-grid,
          .password-rules {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 575px) {
          .settings-page {
            padding: 14px;
          }

          .settings-hero {
            padding: 24px;
            border-radius: 22px;
          }

          .settings-hero h2 {
            font-size: 24px;
          }

          .settings-card-header,
          .settings-card-body {
            padding: 18px;
          }

          .logo-footer {
            flex-direction: column;
            align-items: stretch;
          }

          .settings-actions {
            justify-content: stretch;
          }

          .primary-action,
          .secondary-action {
            width: 100%;
          }
        }
      `}</style>

      <div className="settings-page">
        <div className="settings-hero">
          <div className="settings-eyebrow">Tetapan Portal</div>
          <h2>Keselamatan akaun dan logo agensi</h2>
          <p>
            Kemaskini kata laluan portal dan logo agensi. Logo yang dimuat naik
            akan dipaparkan pada sidebar dan paparan portal.
          </p>
        </div>

        <div className="settings-grid">
          <div className="settings-card">
            <div className="settings-card-header">
              <div>
                <div className="settings-card-kicker">Keselamatan Akaun</div>
                <h4 className="settings-card-title">Kemaskini kata laluan</h4>
                <p className="settings-card-subtitle">
                  Gunakan kata laluan yang kuat dan sukar diteka untuk melindungi
                  akses portal.
                </p>
              </div>
            </div>

            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group">
                  <label htmlFor="oldPassword">Kata Laluan Semasa</label>
                  <div className="password-field">
                    <input
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="settings-input"
                      id="oldPassword"
                      type={showOldPassword ? "text" : "password"}
                      placeholder="Masukkan kata laluan semasa"
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      className="eye-button"
                      onClick={() => setShowOldPassword((prev) => !prev)}
                      aria-label={
                        showOldPassword
                          ? "Sembunyikan kata laluan semasa"
                          : "Paparkan kata laluan semasa"
                      }
                    >
                      {showOldPassword ? "🙈" : "👁"}
                    </button>
                  </div>
                </div>

                <div className="settings-form-group">
                  <label htmlFor="newPassword">Kata Laluan Baharu</label>
                  <div className="password-field">
                    <input
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="settings-input"
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Masukkan kata laluan baharu"
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="eye-button"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      aria-label={
                        showNewPassword
                          ? "Sembunyikan kata laluan baharu"
                          : "Paparkan kata laluan baharu"
                      }
                    >
                      {showNewPassword ? "🙈" : "👁"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="password-rules">
                <PasswordRule valid={passwordRules.minLength}>
                  Sekurang-kurangnya 8 aksara
                </PasswordRule>
                <PasswordRule valid={passwordRules.uppercase}>
                  Ada sekurang-kurangnya satu huruf besar
                </PasswordRule>
                <PasswordRule valid={passwordRules.lowercase}>
                  Ada sekurang-kurangnya satu huruf kecil
                </PasswordRule>
                <PasswordRule valid={passwordRules.digit}>
                  Ada sekurang-kurangnya satu nombor
                </PasswordRule>
              </div>

              <div className="settings-actions">
                <button
                  className="primary-action"
                  type="button"
                  onClick={handleNewPasswordSubmit}
                  disabled={passwordUpdating}
                >
                  {passwordUpdating ? "Sedang dikemaskini..." : "Kemaskini Kata Laluan"}
                </button>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="settings-card-header">
              <div>
                <div className="settings-card-kicker">Penjenamaan Agensi</div>
                <h4 className="settings-card-title">Logo agensi</h4>
                <p className="settings-card-subtitle">
                  Muat naik logo rasmi agensi untuk paparan portal dan menu
                  navigasi.
                </p>
              </div>
            </div>

            <div className="settings-card-body">
              <div className="logo-dropzone">
                <label className="logo-upload-label">
                  <input
                    type="file"
                    className="logo-file-input"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />

                  <div className="logo-upload-icon">+</div>
                  <div className="logo-upload-title">Pilih logo agensi</div>
                  <div className="logo-upload-hint">
                    Gambar PNG, JPG atau SVG adalah digalakkan
                  </div>
                </label>
              </div>

              {(existingLogo || logoPreview) && (
                <div className="logo-preview-panel">
                  <div className="logo-preview-label">
                    {logoPreview ? "Pratonton Logo Baharu" : "Logo Semasa"}
                  </div>

                  <div className="logo-preview-box">
                    <img
                      src={logoPreview || existingLogo}
                      alt={logoPreview ? "Logo Agensi Baharu" : "Logo Agensi Semasa"}
                    />
                  </div>
                </div>
              )}

              <div className="logo-footer">
                <div className="logo-filename">
                  {logoFile ? logoFile.name : "Tiada logo baharu dipilih"}
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  {logoPreview && (
                    <button
                      className="secondary-action"
                      type="button"
                      onClick={clearLogoSelection}
                    >
                      Buang Pilihan
                    </button>
                  )}

                  <button
                    className="primary-action"
                    onClick={handleLogoUpload}
                    disabled={uploading || !logoFile}
                    type="button"
                  >
                    {uploading ? "Sedang dimuat naik..." : "Kemaskini Logo"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const PasswordRule = ({ valid, children }) => {
  return (
    <div className={`password-rule ${valid ? "valid" : ""}`}>
      <span className="rule-dot">{valid ? "✓" : "•"}</span>
      <span>{children}</span>
    </div>
  );
};

export default Settings;